const KLAVIYO_SCRIPT_ID = 'klaviyo-onsite'
const KLAVIYO_SCRIPT_SRC = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=U7wVZm'

// "Active on Site" and other onsite metrics are recorded only for identified visitors.
// Identity is kept in memory until analytics consent loads klaviyo.js, then passed on.
let isKlaviyoLoaded = false
let pendingIdentity = null
// Events from the current visit before consent; sent only if the visitor accepts
const MAX_PENDING_EVENTS = 20
let pendingEvents = []
// `_kx` from a Klaviyo email link; klaviyo.js reads it from the URL only on its own load,
// so it is lost if the visitor navigates before accepting cookies
let emailExchangeId = null

const getUrlExchangeId = () => new URLSearchParams(window.location.search).get('_kx')

// Klaviyo's official stub: queues calls on window.klaviyo until klaviyo.js is ready
const installKlaviyoStub = () => {
  /* eslint-disable */
  !(function () {
    if (!window.klaviyo) {
      window._klOnsite = window._klOnsite || []
      try {
        window.klaviyo = new Proxy(
          {},
          {
            get: function (n, i) {
              return 'push' === i
                ? function () {
                    var n
                    ;(n = window._klOnsite).push.apply(n, arguments)
                  }
                : function () {
                    for (var n = arguments.length, o = new Array(n), w = 0; w < n; w++)
                      o[w] = arguments[w]
                    var t = 'function' == typeof o[o.length - 1] ? o.pop() : void 0,
                      e = new Promise(function (n) {
                        window._klOnsite.push(
                          [i].concat(o, [
                            function (i) {
                              t && t(i), n(i)
                            },
                          ])
                        )
                      })
                    return e
                  }
            },
          }
        )
      } catch (n) {
        ;(window.klaviyo = window.klaviyo || []),
          (window.klaviyo.push = function () {
            var n
            ;(n = window._klOnsite).push.apply(n, arguments)
          })
      }
    }
  })()
  /* eslint-enable */
}

const sendIdentity = (properties) => {
  try {
    window.klaviyo.identify(properties)
  } catch (error) {
    console.warn('[Klaviyo] identify failed', error)
  }
}

// `call` is [method, ...args] on window.klaviyo, e.g. ['track', 'Viewed Product', {...}]
const sendCall = ([method, ...args]) => {
  try {
    window.klaviyo[method](...args)
  } catch (error) {
    console.warn(`[Klaviyo] ${method} failed`, args[0], error)
  }
}

const queueOrSend = (call) => {
  if (isKlaviyoLoaded) {
    sendCall(call)
  } else if (pendingEvents.length < MAX_PENDING_EVENTS) {
    pendingEvents.push(call)
  }
}

export function captureKlaviyoExchangeId() {
  if (typeof window === 'undefined') return

  const exchangeId = getUrlExchangeId()
  if (exchangeId) {
    emailExchangeId = exchangeId
  }
}

export function loadKlaviyo() {
  if (typeof window === 'undefined' || document.getElementById(KLAVIYO_SCRIPT_ID)) return

  installKlaviyoStub()

  const scriptKlaviyo = document.createElement('script')
  scriptKlaviyo.id = KLAVIYO_SCRIPT_ID
  scriptKlaviyo.src = KLAVIYO_SCRIPT_SRC
  scriptKlaviyo.async = true
  document.body.appendChild(scriptKlaviyo)

  isKlaviyoLoaded = true

  if (emailExchangeId && !getUrlExchangeId()) {
    sendIdentity({ $exchange_id: emailExchangeId })
  }

  if (pendingIdentity) {
    sendIdentity(pendingIdentity)
    pendingIdentity = null
  }

  // Identity goes first so these events attach to the profile
  pendingEvents.forEach(sendCall)
  pendingEvents = []
}

/**
 * Links the current browser to a Klaviyo profile so onsite activity is recorded for it.
 * Sent only once analytics consent has loaded klaviyo.js; until then it waits in memory.
 */
export function identifyKlaviyo({ email, ...properties } = {}) {
  if (typeof window === 'undefined') return

  const normalizedEmail = typeof email === 'string' ? email.trim() : ''
  if (!normalizedEmail) return

  const identity = { ...properties, email: normalizedEmail }

  if (!isKlaviyoLoaded) {
    pendingIdentity = identity
    return
  }

  sendIdentity(identity)
}

/**
 * Sends an onsite event. For an anonymous visitor Klaviyo attaches it once they are identified.
 * Waits in memory (current visit only) until analytics consent loads klaviyo.js.
 */
export function trackKlaviyo(metricName, properties = {}) {
  if (typeof window === 'undefined' || !metricName) return

  queueOrSend(['track', metricName, properties])
}

// Feeds Klaviyo's "recently viewed items" (used in email product blocks)
export function trackKlaviyoViewedItem(item) {
  if (typeof window === 'undefined' || !item) return

  queueOrSend(['trackViewedItem', item])
}
